import type { ParentAnalyticsResult, TopicMasteryRow } from "@/hooks/useParentAnalytics";

export type ReadinessStatus = "Emerging" | "Building" | "Strong" | "Advanced";
export type Confidence = "low" | "medium" | "high";

export interface ReadinessDimension {
  id: string;
  label: string;
  matchers: string[];
  whyItMatters: string;
}

export const READINESS_DIMENSIONS: ReadinessDimension[] = [
  {
    id: "money",
    label: "Money Mastery",
    matchers: ["money", "budget", "saving", "spending", "cash", "income"],
    whyItMatters: "Money habits formed in the teen years shape every paycheck after.",
  },
  {
    id: "credit",
    label: "Credit & Debt Awareness",
    matchers: ["credit", "debt", "loan", "interest", "borrow", "card"],
    whyItMatters: "One early credit mistake can shadow rent, car, and home decisions for years.",
  },
  {
    id: "decision",
    label: "Decision-Making Quality",
    matchers: ["decision", "tradeoff", "trade-off", "choice", "risk", "scam", "fraud"],
    whyItMatters: "Strong decision-making compounds across every money, career, and life choice.",
  },
  {
    id: "investing",
    label: "Investing & Future Planning",
    matchers: ["invest", "stock", "portfolio", "compound", "wealth", "retire", "future", "tax"],
    whyItMatters: "Time in the market is the single biggest lever a teen still has on their side.",
  },
  {
    id: "entrepreneurship",
    label: "Entrepreneurship & Ownership",
    matchers: ["entrepreneur", "business", "ownership", "asset", "career", "income stream"],
    whyItMatters: "Ownership thinking turns earned income into long-term independence.",
  },
];

export interface ReadinessRow {
  id: string;
  label: string;
  status: ReadinessStatus | "Insight Building";
  confidence: Confidence;
  score: number; // 0-100, 0 when building
  evidence: string;
  whyItMatters: string;
  nextAction: string;
  sampleSize: number;
}

function matchTopics(topics: TopicMasteryRow[], matchers: string[]): TopicMasteryRow[] {
  const m = matchers.map((s) => s.toLowerCase());
  return topics.filter((t) => {
    const lo = (t.topic || "").toLowerCase();
    return m.some((k) => lo.includes(k));
  });
}

function statusFor(score: number, sample: number): { status: ReadinessRow["status"]; confidence: Confidence } {
  // Confidence rubric — never claim "Strong" / "Advanced" or high confidence with thin data.
  // <3 → insight building (no status), 3-4 → low, 5-7 → medium, 8+ → high.
  if (sample < 3) return { status: "Insight Building", confidence: "low" };
  const confidence: Confidence = sample >= 8 ? "high" : sample >= 5 ? "medium" : "low";
  // Strong / Advanced require BOTH a high score AND a high-confidence sample.
  if (score >= 85 && confidence === "high") return { status: "Advanced", confidence };
  if (score >= 70 && confidence === "high") return { status: "Strong", confidence };
  if (score >= 70) return { status: "Building", confidence };
  if (score >= 45) return { status: "Building", confidence };
  return { status: "Emerging", confidence };
}

export function buildReadinessCompass(analytics: ParentAnalyticsResult): ReadinessRow[] {
  return READINESS_DIMENSIONS.map((dim) => {
    const matched = matchTopics(analytics.topicMastery, dim.matchers);
    const sample = matched.reduce((s, t) => s + (t.quizCount || 0) + (t.lessonsCompleted || 0), 0);
    if (matched.length === 0 || sample === 0) {
      return {
        id: dim.id,
        label: dim.label,
        status: "Insight Building" as const,
        confidence: "low" as const,
        score: 0,
        evidence: "Insight building — once your teen explores lessons in this area, HeirWise will map their readiness here.",
        whyItMatters: dim.whyItMatters,
        nextAction: `Open a lesson tagged to ${dim.label.toLowerCase()} together this week.`,
        sampleSize: 0,
      };
    }
    // Score = blend of mastery + avg quiz score weighted by lesson completion ratio.
    let weightedMastery = 0;
    let weightSum = 0;
    let avgQuiz = 0;
    let qCount = 0;
    for (const t of matched) {
      const w = Math.max(1, t.lessonsCompleted);
      weightedMastery += t.masteryScore * w;
      weightSum += w;
      if (t.quizCount > 0) {
        avgQuiz += t.averageQuizScore * t.quizCount;
        qCount += t.quizCount;
      }
    }
    const masteryAvg = weightSum > 0 ? weightedMastery / weightSum : 0;
    const quizAvg = qCount > 0 ? avgQuiz / qCount : masteryAvg;
    const score = Math.round(masteryAvg * 0.5 + quizAvg * 0.5);
    const { status, confidence } = statusFor(score, sample);
    const top = matched.slice().sort((a, b) => b.masteryScore - a.masteryScore)[0];
    const weakest = matched.slice().sort((a, b) => a.masteryScore - b.masteryScore)[0];
    const evidence =
      status === "Advanced" || status === "Strong"
        ? `Strongest signal in ${top.topic}. ${sample} learning moments tracked so far.`
        : `Recent activity in ${weakest.topic}. ${sample} learning moments tracked so far.`;
    const nextAction =
      status === "Advanced" || status === "Strong"
        ? `Stretch your teen with a harder challenge in ${dim.label.toLowerCase()}.`
        : `Revisit ${weakest.topic} together — ask them to explain the core idea in their own words.`;
    return {
      id: dim.id,
      label: dim.label,
      status,
      confidence,
      score,
      evidence,
      whyItMatters: dim.whyItMatters,
      nextAction,
      sampleSize: sample,
    };
  });
}

// ---------- Decision DNA (derived from quiz behavior — clearly framed as signals, never claims) ----------

export interface DecisionSignal {
  id: string;
  label: string;
  noticed: string;
  whyItMatters: string;
  confidence: Confidence;
  nextAction: string;
  prompt: string;
}

export interface DecisionDNAResult {
  state: "building" | "ready";
  reason?: string;
  signals: DecisionSignal[];
}

export function buildDecisionDNA(analytics: ParentAnalyticsResult): DecisionDNAResult {
  const attempts = analytics.quizAttempts.length;
  if (attempts < 3) {
    return {
      state: "building",
      reason: `Decision DNA is building. Once your teen completes ${3 - attempts} more quiz${attempts === 2 ? "" : "zes"}, HeirWise will begin identifying learning patterns.`,
      signals: [],
    };
  }

  const signals: DecisionSignal[] = [];
  const conf = (sample: number): Confidence => (sample >= 8 ? "high" : sample >= 4 ? "medium" : "low");

  // Tradeoff awareness from first-attempt pass rate
  if (analytics.firstAttemptPassRate >= 75) {
    signals.push({
      id: "tradeoff",
      label: "Tradeoff Awareness",
      noticed: `Early signal: your teen passes ${Math.round(analytics.firstAttemptPassRate)}% of quizzes on their first try.`,
      whyItMatters: "Reading a question carefully before answering is a foundational decision skill.",
      confidence: conf(attempts),
      nextAction: "Ask them to walk you through a recent quiz question they got right.",
      prompt: "What helped you pick the best answer the first time?",
    });
  } else if (analytics.retakeRate >= 30) {
    signals.push({
      id: "iteration",
      label: "Iteration Habit",
      noticed: `Early signal: about ${Math.round(analytics.retakeRate)}% of attempts are retakes — your teen is willing to try again.`,
      whyItMatters: "Retrying after a mistake is one of the strongest predictors of long-term mastery.",
      confidence: conf(attempts),
      nextAction: "Celebrate the retake itself, not just the score.",
      prompt: "What did you change between your first try and your second?",
    });
  }

  // Risk / debt sensitivity from credit-area weakness
  const creditTopics = matchTopics(analytics.topicMastery, ["credit", "debt", "loan", "interest"]);
  const creditAvg = creditTopics.length
    ? creditTopics.reduce((s, t) => s + (t.averageQuizScore || 0), 0) / creditTopics.length
    : 0;
  if (creditTopics.length > 0 && creditAvg > 0 && creditAvg < 70) {
    signals.push({
      id: "debt",
      label: "Debt Sensitivity",
      noticed: `Early signal: credit and debt questions are landing under ${Math.round(creditAvg)}% on average.`,
      whyItMatters: "Understanding how interest compounds is what separates safe credit from costly credit.",
      confidence: conf(creditTopics.reduce((s, t) => s + t.quizCount, 0)),
      nextAction: "Walk through a minimum-payment scenario together this week.",
      prompt: "If you only paid the minimum on a $1,000 card, how long would it really take to pay off?",
    });
  }

  // Long-term thinking from investing-area strength
  const investTopics = matchTopics(analytics.topicMastery, ["invest", "compound", "wealth", "future"]);
  const investAvg = investTopics.length
    ? investTopics.reduce((s, t) => s + (t.averageQuizScore || 0), 0) / investTopics.length
    : 0;
  if (investTopics.length > 0 && investAvg >= 70) {
    signals.push({
      id: "longterm",
      label: "Long-Term Thinking",
      noticed: `Early signal: your teen is grasping investing concepts at around ${Math.round(investAvg)}% accuracy.`,
      whyItMatters: "Long-term thinking is the hardest financial skill to teach — and the most valuable.",
      confidence: conf(investTopics.reduce((s, t) => s + t.quizCount, 0)),
      nextAction: "Ask them to imagine where they want to be at 25.",
      prompt: "If you invested $50/month starting now, what could that become by 25?",
    });
  }

  // Confidence under uncertainty from consistency
  if (analytics.currentStreak >= 3) {
    signals.push({
      id: "consistency",
      label: "Confidence Under Uncertainty",
      noticed: `Early signal: your teen has shown up ${analytics.currentStreak} days in a row.`,
      whyItMatters: "Consistency under uncertainty is what builds compounding confidence in real life.",
      confidence: conf(analytics.currentStreak),
      nextAction: "Name the streak out loud — recognition reinforces the habit.",
      prompt: "What's keeping you coming back this week?",
    });
  }

  if (signals.length === 0) {
    return {
      state: "building",
      reason: "Decision DNA is building. A few more quizzes will let HeirWise surface clear behavior signals.",
      signals: [],
    };
  }

  return { state: "ready", signals };
}

// ---------- Mentor Pulse ----------

export interface MentorInsight {
  mentor: string;
  domain: string;
  insight: string;
  state: "ready" | "building";
}

export function buildMentorPulse(analytics: ParentAnalyticsResult): MentorInsight[] {
  const finance = matchTopics(analytics.topicMastery, ["money", "credit", "debt", "invest", "budget", "saving"]);
  const economics = matchTopics(analytics.topicMastery, ["economic", "scarcity", "incentive", "supply", "demand", "market"]);
  const entrepreneurship = matchTopics(analytics.topicMastery, ["entrepreneur", "business", "ownership", "asset", "career"]);
  const anyActivity =
    analytics.lessonCount > 0 || analytics.quizAttempts.length > 0 || analytics.currentStreak > 0;

  const pickPhrase = (rows: TopicMasteryRow[], domain: string, mentorReady: string, mentorBuilding: string): MentorInsight => {
    if (rows.length === 0 || rows.every((r) => r.quizCount === 0 && r.lessonsCompleted === 0)) {
      return { mentor: mentorBuilding.split("|")[0], domain, insight: mentorBuilding.split("|")[1], state: "building" };
    }
    return { mentor: mentorReady.split("|")[0], domain, insight: mentorReady.split("|")[1], state: "ready" };
  };

  const financeTop = finance.slice().sort((a, b) => b.masteryScore - a.masteryScore)[0];
  const financeWeak = finance.slice().sort((a, b) => a.averageQuizScore - b.averageQuizScore).find((t) => t.quizCount > 0);
  const econTop = economics.slice().sort((a, b) => b.masteryScore - a.masteryScore)[0];
  const entTop = entrepreneurship.slice().sort((a, b) => b.masteryScore - a.masteryScore)[0];

  return [
    pickPhrase(
      finance,
      "Finance & Investing",
      `Aelyra Solari|Your teen is showing early traction in ${financeTop?.topic ?? "money basics"}${financeWeak ? `, but ${financeWeak.topic} still needs reinforcement` : ""}.`,
      "Aelyra Solari|Aelyra is waiting on a first finance lesson before she shares an insight.",
    ),
    pickPhrase(
      economics,
      "Economics",
      `Caelion Veyr|Recent choices show your teen is beginning to read incentives — ${econTop?.topic ?? "core ideas"} is the next anchor.`,
      "Caelion Veyr|Caelion is preparing insights — complete one economics-related lesson to unlock guidance.",
    ),
    pickPhrase(
      entrepreneurship,
      "Entrepreneurship",
      `Headmaster Vaelor|Ownership signals are showing up in ${entTop?.topic ?? "their recent activity"}. Ask them how they'd price one small idea this week.`,
      "Headmaster Vaelor|Vaelor will weigh in once your teen tries an entrepreneurship lesson or simulation.",
    ),
    {
      mentor: "Lumi Vanta",
      domain: "Support & Momentum",
      insight: anyActivity
        ? analytics.currentStreak >= 2
          ? `They stayed consistent ${analytics.currentStreak} days in a row. Celebrate the momentum before pushing the next challenge.`
          : `They've shown up recently — a small nudge this week can turn effort into a streak.`
        : "Lumi is cheering them on — the moment your teen starts, she'll share encouragement here.",
      state: anyActivity ? "ready" : "building",
    },
  ];
}

// ---------- Financial Future Map ----------

export interface FutureStage {
  id: string;
  label: string;
  description: string;
  whyItMatters: string;
  matchers: string[];
  completed: string[];
  currentFocus: string | null;
  nextUnlock: string | null;
  percent: number;
}

const STAGES: { id: string; label: string; description: string; whyItMatters: string; matchers: string[] }[] = [
  {
    id: "foundation",
    label: "Foundation",
    description: "Budgeting, saving, needs vs. wants.",
    whyItMatters: "Every later financial skill rests on these basics.",
    matchers: ["budget", "saving", "spending", "money", "needs"],
  },
  {
    id: "protection",
    label: "Protection",
    description: "Credit, debt, fraud, insurance.",
    whyItMatters: "Most adults lose ground here before they ever build it.",
    matchers: ["credit", "debt", "fraud", "scam", "insurance", "protect"],
  },
  {
    id: "growth",
    label: "Growth",
    description: "Investing, compound interest, career income.",
    whyItMatters: "Time + investing is the lever that compounds for life.",
    matchers: ["invest", "compound", "career", "income", "stock", "wealth"],
  },
  {
    id: "ownership",
    label: "Ownership",
    description: "Entrepreneurship, business, assets.",
    whyItMatters: "Owning income streams is how earnings become independence.",
    matchers: ["entrepreneur", "business", "ownership", "asset"],
  },
  {
    id: "independence",
    label: "Independence",
    description: "Rent, car, taxes, real-world decisions.",
    whyItMatters: "The bridge from teen to adult financial life.",
    matchers: ["rent", "car", "tax", "vehicle", "home", "real-world"],
  },
];

export function buildFutureMap(analytics: ParentAnalyticsResult): FutureStage[] {
  return STAGES.map((s) => {
    const courses = analytics.courseProgress.filter((c) => {
      const lo = (c.courseTitle || "").toLowerCase();
      return s.matchers.some((m) => lo.includes(m));
    });
    const completed = courses.filter((c) => c.status === "Completed").map((c) => c.courseTitle);
    const inProgress = courses.find((c) => c.status === "In Progress");
    const notStarted = courses.find((c) => c.status === "Not Started");
    const totalPercent = courses.length
      ? Math.round(courses.reduce((acc, c) => acc + (c.completionPercentage || 0), 0) / courses.length)
      : 0;
    return {
      id: s.id,
      label: s.label,
      description: s.description,
      whyItMatters: s.whyItMatters,
      matchers: s.matchers,
      completed,
      currentFocus: inProgress?.courseTitle ?? null,
      nextUnlock: notStarted?.courseTitle ?? null,
      percent: totalPercent,
    };
  });
}